import retry from 'async-retry';
import { isNotNil, pickBy } from 'es-toolkit';
import { attemptAsync } from 'es-toolkit/util';
import { rejectAfter, wait } from '../promises';
import { HTTPError, NetworkError, TimeoutError } from './errors';
import { getRetryAfterMs, transformOptions } from './utils';
const defaultRetryOptions = { retries: 2 };
const emptyBodyResponseCodes = new Set([204, 205, 304]);
function getRequestUrl(resource) {
    if (typeof resource === 'string') {
        return resource;
    }
    if (resource instanceof URL) {
        return resource.toString();
    }
    if (typeof Request !== 'undefined' && resource instanceof Request) {
        return resource.url;
    }
}
export function fetchWithRetry(resource, options) {
    const { retries = defaultRetryOptions.retries, factor, minTimeout, maxTimeout, randomize, onRetry, parseAs = 'json', timeout, post, json, form, ...fetchOptions } = options || {};
    const retryOptions = {
        retries,
        factor,
        minTimeout,
        maxTimeout,
        randomize,
        onRetry,
    };
    const requestUrl = getRequestUrl(resource);
    async function retrier(bail) {
        let res;
        const fetchPromise = fetch(resource, transformOptions(fetchOptions, { json: json ?? post, form }));
        if (!timeout) {
            try {
                res = await fetchPromise;
            }
            catch (error) {
                throw new NetworkError(error, { url: requestUrl });
            }
        }
        else {
            try {
                res = await rejectAfter(fetchPromise, timeout);
            }
            catch (error) {
                if (error === 'TIMED_OUT') {
                    return bail(new TimeoutError({ timeoutMs: timeout, url: requestUrl }));
                }
                throw new NetworkError(error, { url: requestUrl });
            }
        }
        const hasBody = !(emptyBodyResponseCodes.has(res.status) || res.headers.get('Content-Length') === '0');
        const [bodyParseError, parsedBody = null] = await attemptAsync(() => hasBody && parseAs ? res[parseAs]?.() : Promise.resolve(null));
        const createHTTPError = () => new HTTPError(res, parsedBody, { cause: bodyParseError ?? undefined, url: requestUrl });
        if (res.ok) {
            if (bodyParseError) {
                throw bodyParseError;
            }
            return parseAs && parsedBody !== null ? parsedBody : res;
        }
        if (res.status === 401) {
            return bail(createHTTPError());
        }
        if (res.status === 429) {
            const retryAfterMs = getRetryAfterMs(res.headers);
            if (retryAfterMs !== undefined) {
                await wait(Math.max(retryAfterMs - (minTimeout ?? 1000), 0));
                throw createHTTPError();
            }
        }
        if (res.status < 500) {
            return bail(createHTTPError());
        }
        throw createHTTPError();
    }
    return retry(retrier, pickBy(retryOptions, isNotNil));
}
